import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { FileText, Download, Upload, Clock, CheckCircle, AlertCircle, Calendar, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const MonDossier = () => {
  const { user, token } = useAuth();
  const [file, setFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchMyFile();
    if (user?.role === 'employee') {
      fetchMyPayslips();
    }
  }, []);

  const fetchMyFile = async () => {
    try {
      if (isMountedRef.current) setLoading(true);
      
      // Get client's file
      const filesResponse = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (filesResponse.data.length > 0 && isMountedRef.current) {
        const myFile = filesResponse.data[0]; // Get first file
        setFile(myFile);
        
        // Get documents - handle errors gracefully
        try {
          const docsResponse = await axios.get(`${API}/documents/${myFile.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (isMountedRef.current) setDocuments(docsResponse.data || []);
        } catch (docError) {
          console.error('Failed to fetch documents:', docError);
          if (isMountedRef.current) setDocuments([]);
        }
        
        // Get activities - handle errors gracefully
        try {
          const activitiesResponse = await axios.get(`${API}/files/${myFile.id}/activity`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (isMountedRef.current) setActivities(activitiesResponse.data || []);
        } catch (actError) {
          console.error('Failed to fetch activities:', actError);
          if (isMountedRef.current) setActivities([]);
        }
      }
      
      if (isMountedRef.current) setLoading(false);
    } catch (error) {
      console.error('Failed to fetch file:', error);
      if (isMountedRef.current) {
        toast.error('Erreur lors du chargement de votre dossier');
        setLoading(false);
      }
    }
  };

  const fetchMyPayslips = async () => {
    try {
      const response = await axios.get(`${API}/payroll`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayslips(response.data || []);
    } catch (error) {
      console.error('Failed to fetch payslips:', error);
      setPayslips([]);
    }
  };

  const handleDownloadPayslip = async (payrollId) => {
    try {
      const response = await axios.post(
        `${API}/payroll/generate-payslip?payroll_id=${payrollId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulletin_paie_${payrollId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Bulletin de paie téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (!file) {
      toast.error('Aucun dossier trouvé');
      return;
    }

    setUploading(true);
    
    for (const uploadFile of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('file_id', file.id);
        formData.append('category', 'documents_client');

        await axios.post(
          `${API}/documents/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        toast.success(`${uploadFile.name} téléversé avec succès`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Erreur lors du téléversement: ${error.response?.data?.detail || uploadFile.name}`);
      }
    }
    
    setUploading(false);
    fetchMyFile();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: uploading
  });

  const handleDownload = async (docId, docName) => {
    try {
      const response = await axios.get(`${API}/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = docName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Document téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      ouvert: { label: 'Ouvert', color: 'bg-blue-100 text-blue-800', icon: Clock },
      en_cours: { label: 'En Cours', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      soumission_ircc: { label: 'Soumis à IRCC', color: 'bg-purple-100 text-purple-800', icon: TrendingUp },
      en_traitement: { label: 'En Traitement', color: 'bg-orange-100 text-orange-800', icon: Clock },
      decision: { label: 'Décision', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      ferme: { label: 'Fermé', color: 'bg-gray-100 text-gray-800', icon: CheckCircle }
    };
    return statusMap[status] || statusMap.ouvert;
  };

  const calculateProgress = () => {
    if (!file) return 0;
    const steps = Object.values(file.progress_steps || {});
    const completed = steps.filter(Boolean).length;
    return (completed / steps.length) * 100;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  if (!file) {
    return (
      <Layout>
        <Card className="border-0 shadow-xl">
          <CardContent className="p-12 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Aucun Dossier</h3>
            <p className="text-gray-600">
              Vous n'avez pas encore de dossier. Veuillez contacter votre conseiller.
            </p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const statusInfo = getStatusInfo(file.status);
  const StatusIcon = statusInfo.icon;
  const progress = calculateProgress();

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">📁 Mon Dossier</h1>
          <p className="text-lg text-gray-600">Suivez l'avancement de votre dossier d'immigration</p>
        </div>

        {/* File Info Card */}
        <Card className="border-0 shadow-xl bg-gradient-to-br from-red-50 to-blue-50">
          <CardContent className="p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{file.file_number}</h2>
                <p className="text-gray-600">Dossier ouvert le {format(parseISO(file.created_at), 'dd MMMM yyyy', { locale: fr })}</p>
              </div>
              <Badge className={`${statusInfo.color} px-4 py-2 text-lg flex items-center gap-2`}>
                <StatusIcon className="h-5 w-5" />
                {statusInfo.label}
              </Badge>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-gray-700">Progression du dossier</span>
                <span className="font-bold text-red-600">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-3" />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Documents Section */}
          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-red-600 to-blue-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-6 w-6" />
                Documents ({documents.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Upload Zone */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 transition-colors cursor-pointer ${
                  isDragActive
                    ? 'border-red-600 bg-red-50'
                    : 'border-gray-300 hover:border-red-600 hover:bg-gray-50'
                } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input {...getInputProps()} />
                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-700 font-semibold mb-1">
                  {uploading ? 'Téléversement en cours...' : 'Glissez vos documents ici'}
                </p>
                <p className="text-sm text-gray-500">
                  ou cliquez pour sélectionner des fichiers
                </p>
              </div>

              {/* Documents List */}
              <div className="space-y-3">
                {documents.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Aucun document</p>
                ) : (
                  documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <FileText className="h-5 w-5 text-red-600" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{doc.name}</p>
                          <p className="text-xs text-gray-500">
                            {format(parseISO(doc.uploaded_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(doc.id, doc.name)}
                        className="ml-2"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                Historique ({activities.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {activities.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Aucune activité</p>
                ) : (
                  activities.slice(0, 10).map((activity) => (
                    <div key={activity.id} className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="h-2 w-2 mt-2 rounded-full bg-purple-600"></div>
                      </div>
                      <div className="flex-1 pb-4 border-b border-gray-100 last:border-0">
                        <p className="font-medium text-gray-900">{activity.action}</p>
                        <p className="text-sm text-gray-600">{activity.description}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {format(parseISO(activity.timestamp), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Steps */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>Étapes du Dossier</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {Object.entries(file.progress_steps || {}).map(([key, completed]) => (
                <div
                  key={key}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    completed
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    {completed ? (
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    ) : (
                      <Clock className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <p className="text-sm font-semibold text-gray-900 capitalize">
                    {key.replace(/_/g, ' ')}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Payslips Section - Only for Employees */}
        {user?.role === 'employee' && payslips.length > 0 && (
          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-600 to-blue-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-6 w-6" />
                Mes Bulletins de Paie ({payslips.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-3">
                {payslips.map((payslip) => (
                  <div
                    key={payslip.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <FileText className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          Bulletin de Paie - {payslip.period}
                        </p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {payslip.period}
                          </span>
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-4 w-4" />
                            Net: {payslip.net_salary?.toFixed(2) || '0.00'} $
                          </span>
                        </div>
                        {payslip.status && (
                          <Badge className={`mt-2 ${
                            payslip.status === 'paye' ? 'bg-green-500' :
                            payslip.status === 'valide' ? 'bg-blue-500' :
                            'bg-gray-500'
                          } text-white`}>
                            {payslip.status === 'paye' ? 'Payé' :
                             payslip.status === 'valide' ? 'Validé' :
                             'Brouillon'}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDownloadPayslip(payslip.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default MonDossier;
